import { useState } from 'react';
import { ContentCentralCatalogPicker } from './ContentCentralCatalogPicker';
import { ContentCentralCheck } from './ContentCentralCheck';
import { ContentCentralNewCatalog } from './ContentCentralNewCatalog';
import { ContentCentralTypeMap, typeSelectId } from './ContentCentralTypeMap';
import { ContentCentralWaiting } from './ContentCentralWaiting';
import type { FieldsProps } from './DestinationFields';
import type { PaigeDestinationsBridge } from './paige.global';
import { choicesOf, useCatalog } from './use-content-central';

export interface ContentCentralSetupProps extends FieldsProps {
  bridge: PaigeDestinationsBridge;
  /** The stored destination: its key is Paige's, so every Content Central call is made with it. */
  destinationId: string;
}

/** "Review matching": the type's Content Central choice brought into view and focused. */
function review(type: string) {
  const select = document.getElementById(typeSelectId(type));
  select?.scrollIntoView?.({ block: 'center' });
  select?.focus();
}

/**
 * A stored Content Central destination's setup, as CapturePoint's Catalog and Match steps: the catalog (picked
 * from what the key can see, or created from this job), the documents waiting for a match, the type map read from
 * the catalog's own types and fields, and Check with Content Central. Nothing reaches Content Central but reads and
 * the check until Create catalog; Save keeps the choices.
 */
export function ContentCentralSetup(props: ContentCentralSetupProps) {
  const { bridge, destinationId, draft, setTypes, choices } = props;
  const { definition, error } = useCatalog(bridge, destinationId, draft.catalog);
  const chosen = draft.catalog.trim() !== '';
  return (
    <>
      <CatalogArea {...props} name={definition?.name ?? ''} />
      {error && <small className="paige-destination-error">{error}</small>}
      <ContentCentralWaiting bridge={bridge} destinationId={destinationId} onReview={review} />
      {setTypes && chosen && (
        <ContentCentralTypeMap
          types={draft.types}
          onChange={setTypes}
          choices={{ ...choices, ...choicesOf(definition) }}
        />
      )}
      {chosen && (
        <ContentCentralCheck
          bridge={bridge}
          destinationId={destinationId}
          catalog={draft.catalog}
          types={draft.types}
        />
      )}
    </>
  );
}

/** The catalog in use and its picker, or the create-catalog confirm in its place. */
function CatalogArea(props: ContentCentralSetupProps & { name: string }) {
  const { bridge, destinationId, draft, set, name } = props;
  const [creating, setCreating] = useState(false);
  const choose = (catalog: { id: string }) => set('catalog')({ target: { value: catalog.id } });
  if (creating)
    return (
      <ContentCentralNewCatalog
        bridge={bridge}
        destinationId={destinationId}
        onCreated={(catalog) => (setCreating(false), choose(catalog))}
        onClose={() => setCreating(false)}
      />
    );
  return (
    <ContentCentralCatalogPicker
      bridge={bridge}
      destinationId={destinationId}
      current={draft.catalog.trim() ? { id: draft.catalog, name } : null}
      onChoose={choose}
      onCreate={() => setCreating(true)}
    />
  );
}
